import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useMemo, useRef } from 'react';
import SoftPullStatus from '~/constants/softPullStatus';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useShouldDisplayOrderStatus from '~/hooks/vdp/selectors/useShouldDisplayOrderStatus';
import useSoftPullStatus from '~/hooks/vdp/selectors/useSoftPullStatus';
import { PricingError } from '~/services/srp/contracts/PricingResponse';

export default function useSendSptEventAndActions() {
  const { vehicleId = 0 } = useGetVehicleDetails();
  const {
    vdpPricingInfo: { data, isLoaded },
    vdpPricingError
  } = useContext(UserVehicleContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { softPullStatus } = useSoftPullStatus();
  const hasSoftPull = softPullStatus === SoftPullStatus.Active;
  const sendData =
    vdpPricingError === PricingError.None ||
    vdpPricingError === PricingError.NoVehiclePricingInfo;
  const shouldDisplayOrderStatus = useShouldDisplayOrderStatus();

  const hasSoftPullRef = useRef<boolean | null>(null);

  useEffectOnce(
    () => {
      hasSoftPullRef.current = softPullStatus === SoftPullStatus.Active;
    },
    [softPullStatus],
    isLoaded
  );

  const eventData = useMemo(
    () =>
      sendData
        ? {
            priceInfo: data.price,
            transportCost: data.price?.transportCost,
            hasSoftPull,
            isTradeInApplied: data.tradeInfo?.applyTradeIn ?? null,
            error: vdpPricingError
          }
        : {
            error: vdpPricingError
          },
    [
      data.price,
      data.tradeInfo?.applyTradeIn,
      hasSoftPull,
      sendData,
      vdpPricingError
    ]
  );

  useEffectOnce(
    () => {
      trackEvent('SPT Data', eventData);
    },
    [eventData, trackEvent],
    isLoaded
  );

  useEffectOnce(
    () => {
      trackEvent('SPT Data', eventData);
    },
    [eventData, trackEvent],
    hasSoftPullRef.current !== null && hasSoftPullRef.current !== hasSoftPull
  );

  useEffect(() => {
    if (!shouldDisplayOrderStatus) return;

    datadogRum.addAction('SPT displayed order status card');
  }, [shouldDisplayOrderStatus]);

  useEffect(() => {
    if (!isLoaded) return;

    datadogRum.addAction('SPT Health', { status: vdpPricingError, vehicleId });
  }, [isLoaded, vdpPricingError, vehicleId]);
}
